import { Injectable, OnDestroy } from '@angular/core';
import { DeferredPromise, sleep } from '@cf-shared/shared-util';
import { PageEnteredEvent } from 'app/app.component';
import { CsBackdropComponent } from 'components/cs-backdrop/cs-backdrop';
import { AnimationController } from 'providers/animation-controller';
import { waitHtmlEvent } from 'services/browser-utils';
import { LoggingService } from 'services/logging.service';
import { PubSubRegistration, PubSubService } from 'services/pubsub.service';

interface BackdropOptions {
  disableDismissOnClick?: boolean;
}

interface SimpleBackdropOptions {
  onClick?: () => void;
  disableDismissOnClick?: boolean;
  invisible?: boolean;
}

@Injectable()
export class BackdropController implements OnDestroy {
  backdropComponent: CsBackdropComponent = undefined;
  coverClickedPromise: DeferredPromise<void> = undefined;
  pageEnterSub: PubSubRegistration;
  options: BackdropOptions;

  constructor(
    public logging: LoggingService,
    private pubSub: PubSubService,
    private animationController: AnimationController,
  ) {
    // auto-hide the backdrop if a page transition occurs without user interaction (can happen on back button, etc)
    this.pageEnterSub = this.pubSub.subscribe(PageEnteredEvent, () => this.onCoverClicked());
  }

  ngOnDestroy() {
    if (this.pageEnterSub) {
      this.pageEnterSub.unsubscribe();
    }
  }

  public registerHost(host: CsBackdropComponent) {
    this.backdropComponent = host;
  }

  // Show the backdrop and reparent a particular element to it. When the backdrop is clicked, reparent the element back where it was and hide the backdrop.
  // the element has the "popout" class added to it while it is in this state
  public async showBackdropAsync(popoutElement: HTMLElement, opts?: BackdropOptions) {
    this.backdropComponent.setFadeout(false);
    this.backdropComponent.setActive(true);

    this.options = opts || {};

    // remember where the element was before we reparent it
    let oldParent = popoutElement.parentElement;
    let oldNextSibling = popoutElement.nextSibling;
    let oldBox = popoutElement.getBoundingClientRect();

    // reparent the element to the backdrop
    this.backdropComponent.backdrop.nativeElement.appendChild(popoutElement);

    // insert a dummy div in its place to preserve the layout
    let dummy = document.createElement('div');
    dummy.style.width = `${oldBox.width}px`;
    dummy.style.height = `${oldBox.height}px`;
    dummy.style.display = 'inline-block';
    dummy.style.verticalAlign = 'top';
    oldParent.insertBefore(dummy, oldNextSibling);

    // position the element at the same place it was before
    popoutElement.style.position = 'fixed';
    popoutElement.style.right = `${document.body.clientWidth - oldBox.right}px`;
    popoutElement.style.bottom = `${document.body.clientHeight - oldBox.bottom}px`;

    if (this.coverClickedPromise) {
      this.logging.trackAbnormalError('BackdropController.showBackdropAsync() called while another backdrop is already active');
    }
    this.coverClickedPromise = DeferredPromise.defer<void>();

    await sleep(0); // wait for the DOM to update
    popoutElement.classList.add('popout');

    await this.coverClickedPromise;

    this.backdropComponent.setFadeout(true);

    await sleep(0);

    // wait for the popout fadeout animation to finish
    popoutElement.classList.remove('popout');
    if (popoutElement.style.transition) {
      await waitHtmlEvent(popoutElement, 'transitionend');
    }
    let backdropCover = this.backdropComponent.backdrop.nativeElement as HTMLElement;
    if (backdropCover && 'getAnimations' in backdropCover) {
      const animations = (backdropCover as any).getAnimations() as Animation[];
      if (animations.length > 0) {
        await this.animationController.safeAwaitAnimations(animations);
      }
    }
    // remove the dummy div
    oldParent.removeChild(dummy);
    // restore the element to its original position and parent
    popoutElement.style.position = '';
    popoutElement.style.right = '';
    popoutElement.style.bottom = '';
    oldParent.insertBefore(popoutElement, oldNextSibling);
    this.backdropComponent.setActive(false);
  }

  public dismissBackdropAsync() {
    if (this.coverClickedPromise) {
      this.coverClickedPromise.resolve(null);
      this.coverClickedPromise = undefined;
    }
  }

  public onCoverClicked() {
    // Handle simple backdrop click
    if (this.simpleBackdropClickHandler) {
      this.simpleBackdropClickHandler();
      this.simpleBackdropClickHandler = undefined;
      return;
    }
    // Handle normal backdrop click
    this.dismissBackdropAsync();
  }

  // Simple backdrop methods for showing/hiding without reparenting
  private simpleBackdropClickHandler: (() => void) | undefined;
  
  public showSimpleBackdrop(options?: SimpleBackdropOptions) {
    if (!this.backdropComponent) {
      console.error('BackdropController: backdropComponent not registered');
      return;
    }
    this.backdropComponent.setInvisible(options?.invisible || false);
    this.backdropComponent.setFadeout(false);
    this.backdropComponent.setActive(true);
    this.options = options || {};
    this.simpleBackdropClickHandler = options?.onClick;
  }
  
  public hideSimpleBackdrop() {
    if (!this.backdropComponent) {
      console.error('BackdropController: backdropComponent not registered');
      return;
    }
    this.backdropComponent.setFadeout(true);
    setTimeout(() => {
      if (this.backdropComponent) {
        this.backdropComponent.setActive(false);
        this.backdropComponent.setInvisible(false); // Reset invisible state
      }
      this.simpleBackdropClickHandler = undefined;
    }, 200); // Match the fadeout animation duration
  }

}
